import { useEffect, useRef, useState } from 'preact/hooks';
import {
  type ReviewerCache,
  clearReviewer,
  displayNameFor,
  getIdentity,
  setReviewer as persistReviewer,
} from '../identity';
import { exchangeIframeHandoff, exchangeUrlHandoff } from '../api';
import { tryIframeHandshake } from '../lib/handshake';
import { dashboardOriginFrom } from '../lib/origin';

export interface SessionConfig {
  token: string;
  apiUrl: string;
  demoMode: boolean;
  initialAuthHandoffJwt: string | null;
}

export interface WidgetSession {
  reviewer: ReviewerCache | null;
  loggedIn: boolean;
  canPickWithoutLogin: boolean;
  reviewerName: string;
  ownTokenRef: { current: string };
  /** Commits a reviewer profile (returned by an auth handoff exchange) to
   *  both the cookie cache and the in-memory hook state. */
  persistAuth: (reviewer: ReviewerCache) => void;
  clearSession: () => void;
}

export function useWidgetSession(config: SessionConfig): WidgetSession {
  // Reviewer profile sourced from the cookie cache (populated by a successful
  // auth-handoff exchange or iframe handshake). `null` means "not signed in".
  const initialIdentity = getIdentity();
  const [reviewer, setReviewerState] = useState<ReviewerCache | null>(initialIdentity.reviewer);
  const ownTokenRef = useRef<string>(initialIdentity.creatorToken);

  const loggedIn = reviewer !== null;
  // demoMode has no concept of a confirmed reviewer — there's no backend to
  // confirm against. Treat the user as "free to pin" so they aren't trapped
  // behind a login wall that points nowhere.
  const canPickWithoutLogin = config.demoMode || loggedIn;
  const reviewerName = reviewer ? displayNameFor(reviewer) : '';

  // ─── Auth handoff on mount ────────────────────────────────────────────
  // Two paths can sign the user in without a fresh magic-link round trip:
  //
  //   1. URL JWT carried in ?pinmerge_auth=… by the magic-link callback.
  //      Already stripped from the URL by main.ts; we get it via config.
  //   2. Iframe handshake against the dashboard. Catches users (especially
  //      project owners) who already have a dashboard session in the same
  //      browser. Chrome/Edge only — silently fails in Safari/Firefox.
  //
  // Both end at the same place: `setReviewerState` + persistReviewer. After
  // that the UI flips from "login wall" to the real composer.
  useEffect(() => {
    if (config.demoMode) return;
    let cancelled = false;
    const persist = (r: ReviewerCache) => {
      if (cancelled) return;
      persistReviewer(r);
      setReviewerState(r);
    };

    if (config.initialAuthHandoffJwt) {
      exchangeUrlHandoff(config.apiUrl, {
        token: config.token,
        creatorToken: ownTokenRef.current,
        url: window.location.href,
        jwt: config.initialAuthHandoffJwt,
      })
        .then(persist)
        .catch((err) => {
          console.warn('[pinmerge] url handoff exchange failed', err);
        });
      return () => {
        cancelled = true;
      };
    }

    if (initialIdentity.reviewer) return; // already signed in

    // Layer 2 only fires when no URL JWT was present. Best-effort — we don't
    // surface failures to the user; the login wall is always available as a
    // manual fallback.
    tryIframeHandshake({
      dashboardOrigin: dashboardOriginFrom(config.apiUrl),
      widgetToken: config.token,
    })
      .then(async (result) => {
        if (cancelled || !result) return;
        const r = await exchangeIframeHandoff(config.apiUrl, {
          token: config.token,
          creatorToken: ownTokenRef.current,
          url: window.location.href,
          jwt: result.bindToken,
        });
        persist(r);
      })
      .catch((err) => {
        console.warn('[pinmerge] iframe handshake failed', err);
      });

    return () => {
      cancelled = true;
    };
    // Run-once on mount: deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function persistAuth(r: ReviewerCache) {
    persistReviewer(r);
    setReviewerState(r);
  }

  function clearSession() {
    clearReviewer();
    setReviewerState(null);
  }

  return {
    reviewer,
    loggedIn,
    canPickWithoutLogin,
    reviewerName,
    ownTokenRef,
    persistAuth,
    clearSession,
  };
}
